/**
 * Tutorial state API client (TUTORIAL-STATE-001, 2026-09-21).
 *
 * Two routes, both on `/api/me` because this is the caller's own state:
 *
 *   GET  /api/me/tutorial          → has this person been shown round yet
 *   POST /api/me/tutorial/event    → record one transition
 *
 * WHY THE SERVER HOLDS THIS AND NOT localStorage. Three reasons, in order of
 * how much they matter:
 *
 *   1. A new laptop is not a new person. Browser storage would offer the
 *      tutorial again on every machine and every profile, to somebody who
 *      finished it months ago.
 *   2. Resume. `lastStepKey` lets a tutorial abandoned at step 4 carry on from
 *      step 4, which browser storage cannot do across devices.
 *   3. It is answerable. "How many of this tenant's users have taken the
 *      tour" is a question somebody will eventually ask, and localStorage
 *      cannot be asked anything.
 *
 * Types mirror `apps/api/src/services/tutorial/tutorialService.ts`. See
 * WIRE-TYPE-DUPLICATION-001 in SPEC-46 §8.3 — the platform declares wire types
 * on both sides and `apiFetch` casts rather than validates, so these are an
 * assertion, not a check.
 */

import { apiFetch } from './api';

export type TutorialStatus = 'unseen' | 'offered' | 'running' | 'done' | 'declined';

export interface TutorialState {
  tutorialKey: string;
  status: TutorialStatus;
  /** Where they got to. Compared only against the local step list. */
  lastStepKey: string | null;
  offeredCount: number;
  startedAt: string | null;
  completedAt: string | null;
  /** Derived server-side: should the invitation be on screen now? */
  shouldOffer: boolean;
}

export type TutorialEvent =
  | 'offered'
  | 'deferred'
  | 'declined'
  | 'started'
  | 'step'
  | 'completed';

/** React Query key. One place, so no writer refreshes a key nothing fetches. */
export const TUTORIAL_STATE_KEY = ['me', 'tutorial'] as const;

export async function fetchTutorialState(signal?: AbortSignal): Promise<TutorialState> {
  return apiFetch<TutorialState>('/api/me/tutorial', { signal });
}

export async function recordTutorialEvent(input: {
  event: TutorialEvent;
  stepKey?: string;
}): Promise<TutorialState> {
  return apiFetch<TutorialState>('/api/me/tutorial/event', {
    method: 'POST',
    body: input,
  });
}
